/**
 * useTechnicianData Hook
 *
 * Custom hook for fetching and managing technician performance data.
 * Includes loading/error state management and sorting functionality.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getTechnicianPerformance,
  refreshTechnicianData,
  TechnicianPerformanceResponse,
} from '../services/api';
import {
  Technician,
  TechDailyEntry,
  TechnicianSummary,
  SortConfig,
} from '../components/forecast/TechnicianPerformance/types';

interface UseTechnicianDataParams {
  locationId: number;
  year: number;
  month: number;
  enabled: boolean;
}

interface UseTechnicianDataResult {
  technicians: Technician[];
  dailyByTech: Record<string, TechDailyEntry[]>;
  summary: TechnicianSummary | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  refresh: () => Promise<void>;
}

function transformTechnician(raw: TechnicianPerformanceResponse['technicians'][0]): Technician {
  return {
    techId: raw.techId,
    techName: raw.techName,
    techRate: raw.techRate,
    locationId: raw.locationId,
    year: raw.year,
    month: raw.month,
    flatRateHoursTotal: raw.flatRateHoursTotal,
    laborSalesTotal: raw.laborSalesTotal,
    laborCostTotal: raw.laborCostTotal,
    laborGrossTotal: raw.laborGrossTotal,
    elrTotal: raw.elrTotal,
    costPerHourTotal: raw.costPerHourTotal,
    grossPerHourTotal: raw.grossPerHourTotal,
  };
}

function transformSummary(
  raw: TechnicianPerformanceResponse['summary'],
  locationId: number,
  year: number,
  month: number
): TechnicianSummary | null {
  if (!raw) return null;

  return {
    locationId,
    year,
    month,
    techCount: raw.techCount,
    totalFlatRateHours: raw.totalFlatRateHours,
    totalLaborSales: raw.totalLaborSales,
    totalLaborCost: raw.totalLaborCost,
    totalLaborGross: raw.totalLaborGross,
    lastUpdated: raw.lastUpdated,
  };
}

export function useTechnicianData({
  locationId,
  year,
  month,
  enabled,
}: UseTechnicianDataParams): UseTechnicianDataResult {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [dailyByTech, setDailyByTech] = useState<Record<string, TechDailyEntry[]>>({});
  const [summary, setSummary] = useState<TechnicianSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'laborGrossTotal',
    direction: 'desc',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getTechnicianPerformance(locationId, year, month);

      if (response.success && response.data) {
        const transformed = response.data.technicians.map(transformTechnician);
        setTechnicians(transformed);
        setDailyByTech(response.data.dailyByTech || {});
        setSummary(transformSummary(response.data.summary, locationId, year, month));
      } else {
        setError(response.error || 'Failed to load technician data');
        setTechnicians([]);
        setDailyByTech({});
        setSummary(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setTechnicians([]);
      setDailyByTech({});
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [locationId, year, month]);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setTechnicians([]);
      setDailyByTech({});
      setSummary(null);
      setError(null);
      return;
    }

    fetchData();
  }, [locationId, year, month, enabled, fetchData]);

  const refresh = useCallback(async () => {
    if (!enabled || locationId === 0) return;

    setRefreshing(true);
    setError(null);

    try {
      const refreshResponse = await refreshTechnicianData(locationId, year, month);
      if (!refreshResponse.success) {
        setError(refreshResponse.error || 'Failed to refresh technician data');
        return;
      }

      // Re-fetch the updated data from DynamoDB
      const response = await getTechnicianPerformance(locationId, year, month);
      if (response.success && response.data) {
        const transformed = response.data.technicians.map(transformTechnician);
        setTechnicians(transformed);
        setDailyByTech(response.data.dailyByTech || {});
        setSummary(transformSummary(response.data.summary, locationId, year, month));
      } else {
        setError(response.error || 'Failed to load updated technician data');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during refresh');
    } finally {
      setRefreshing(false);
    }
  }, [locationId, year, month, enabled]);

  const sortedTechnicians = useMemo(() => {
    if (!technicians.length) return [];

    return [...technicians].sort((a, b) => {
      const { field, direction } = sortConfig;
      let comparison = 0;

      if (field === 'name') {
        comparison = a.techName.localeCompare(b.techName);
      } else {
        const aVal = a[field as keyof Technician] as number ?? 0;
        const bVal = b[field as keyof Technician] as number ?? 0;
        comparison = (aVal as number) - (bVal as number);
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [technicians, sortConfig]);

  const handleSortChange = useCallback((config: SortConfig) => {
    setSortConfig(config);
  }, []);

  return {
    technicians: sortedTechnicians,
    dailyByTech,
    summary,
    loading,
    refreshing,
    error,
    sortConfig,
    setSortConfig: handleSortChange,
    refresh,
  };
}
